/**
 * Report AI Chat - AI-powered editing via natural language
 *
 * Floating button that opens a side panel where users can describe
 * changes in natural language. The AI modifies the report HTML.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useAiEditAssessmentReport } from '../team/useAssessmentReports';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ReportAiChatProps {
  reportId: number;
  isLocked: boolean;
  currentHtml: string;
  onApplyEdit: (html: string) => void;
  onSaveEdit: (html: string) => void;
  onRevert: () => void;
  hasPendingEdit: boolean;
  isSaving: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function ReportAiChat({
  reportId,
  isLocked,
  currentHtml,
  onApplyEdit,
  onSaveEdit,
  onRevert,
  hasPendingEdit,
  isSaving,
  onOpenChange,
}: ReportAiChatProps) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  }, [onOpenChange]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const aiEdit = useAiEditAssessmentReport();

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = useCallback(async () => {
    const instruction = input.trim();
    if (!instruction || aiEdit.isPending) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: instruction }]);

    try {
      const result = await aiEdit.mutateAsync({ id: reportId, instruction });
      const newHtml = result.data.htmlContent;

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'De wijziging is toegepast. Bekijk het resultaat en klik op "Opslaan" om te bewaren, of "Ongedaan maken" om terug te gaan.' },
      ]);

      onApplyEdit(newHtml);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Er ging iets mis bij het verwerken van je verzoek. Probeer het opnieuw met een andere omschrijving.',
        },
      ]);
    }
  }, [input, reportId, aiEdit, onApplyEdit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSaveAndClear = () => {
    onSaveEdit(currentHtml);
    setMessages([]);
  };

  if (isLocked) return null;

  return (
    <>
      {/* Floating trigger button */}
      {!isOpen && (
        <button
          onClick={() => toggleOpen(true)}
          className="no-print fixed bottom-6 right-6 z-50 px-4 py-3 bg-purple-600 text-white rounded-full shadow-xl hover:bg-purple-700 transition-all flex items-center gap-2 hover:scale-105"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
          AI Bewerken
        </button>
      )}

      {/* Side panel */}
      {isOpen && (
        <div className="no-print fixed top-0 right-0 bottom-0 w-[400px] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
          {/* Panel header */}
          <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span className="font-medium">AI Bewerken</span>
            </div>
            <button
              onClick={() => toggleOpen(false)}
              className="p-1 hover:bg-purple-700 rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Pending edit bar */}
          {hasPendingEdit && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
              <span className="text-sm text-amber-800 font-medium">Wijziging in afwachting</span>
              <div className="flex gap-2">
                <button
                  onClick={onRevert}
                  className="px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Ongedaan maken
                </button>
                <button
                  onClick={handleSaveAndClear}
                  disabled={isSaving}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSaving ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </div>
          )}

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-8">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-sm font-medium mb-1">Beschrijf je wijziging</p>
                <p className="text-xs text-gray-400">
                  Bijvoorbeeld: "Maak de conclusie positiever" of "Voeg een punt toe over teamwork"
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-sm break-words ${
                    msg.role === 'user'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {aiEdit.isPending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin text-purple-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Bezig met verwerken...
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t border-gray-200 p-3 flex-shrink-0">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Beschrijf de gewenste wijziging..."
                rows={2}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || aiEdit.isPending}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed self-end"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
